import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { aboutInfo } from '@/mocks/data';
import { aboutSchema, breadcrumbSchema } from '@/mocks/schema';

export default function AboutPage() {
  useEffect(() => {
    const scriptId = 'schema-about';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        aboutSchema,
        breadcrumbSchema([
          { name: '首页', url: 'https://91.n' },
          { name: '关于我们', url: 'https://91.n/about' },
        ])
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  return (
    <>
      <SEOHead title="关于我们" description="91.n 是一个致力于为用户提供高效、安全、便捷网络导航服务的专业平台。了解我们的品牌故事与服务承诺。" />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-16 bg-background-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Hero */}
          <section className="text-center py-12 md:py-16">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground-950 mb-4">关于 {aboutInfo.brandName}</h1>
            <p className="text-foreground-600 text-lg max-w-2xl mx-auto leading-relaxed">{aboutInfo.brandIntro}</p>
          </section>

          {/* Mission */}
          <section className="bg-primary-500 rounded-2xl p-8 md:p-12 text-center mb-12">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
              <i className="ri-flag-line text-2xl text-background-50"></i>
            </div>
            <p className="text-xl md:text-2xl text-background-50 font-medium leading-relaxed max-w-2xl mx-auto">
              &ldquo;{aboutInfo.mission}&rdquo;
            </p>
          </section>

          {/* Stats */}
          <section className="mb-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutInfo.stats.map((stat, index) => (
                <div key={index} className="text-center bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <p className="text-3xl md:text-4xl font-bold text-primary-500 mb-1">{stat.value}</p>
                  <p className="text-foreground-500 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 text-center">品牌信誉</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {aboutInfo.reputation.map((item, index) => (
                <div key={index} className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className={`text-accent-600 text-lg ${index === 0 ? 'ri-award-line' : index === 1 ? 'ri-heart-line' : index === 2 ? 'ri-shield-check-line' : 'ri-team-line'}`}></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 text-base">{item.title}</h3>
                  </div>
                  <p className="text-foreground-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="bg-background-100 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 text-center">联系我们</h2>
            <div className="max-w-lg mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-primary-600"></i>
                  </div>
                  <p className="text-sm text-foreground-500 mb-1">电子邮箱</p>
                  <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                    {aboutInfo.contact.email}
                  </a>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-wechat-line text-xl text-accent-600"></i>
                  </div>
                  <p className="text-sm text-foreground-500 mb-1">微信公众号</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-chat-1-line text-sm text-primary-600"></i>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed">{aboutInfo.contact.feedback}</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}